'use client'

import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo } from 'react'

import { useIdentity } from '@/lib/hooks/useIdentity'
import { resolveResourceState } from '@/lib/resources/personalized-resource'
import { PersonalizedResource, ResourceState } from '@/lib/types/personalized-resource'

export type UsePersonalizedResourceResult<T> = ResourceState<T> & {
  /**
   * Looks up a single item, for consumers that query the list rather than
   * render it.
   *
   * Returns undefined unless someone is signed in. The three continue-watching
   * lookups each had to join that themselves, and two of them got it wrong: the
   * `profile !== undefined` check was always true, so signed-out visitors were
   * matched against another session's leftover sessionStorage entries.
   */
  find: (predicate: (item: T) => boolean) => T | undefined

  /**
   * Whether the fetch has finished, however it finished. False before it starts.
   * Distinct from `status !== 'loading'`, which is true while still idle.
   */
  isSettled: boolean
}

/** Reads a personalized resource. */
export function usePersonalizedResource<T>(
  resource: PersonalizedResource<T>,
): UsePersonalizedResourceResult<T> {
  const { isSignedIn, isResolving } = useIdentity()
  const items = useAtomValue(resource.itemsAtom)
  const status = useAtomValue(resource.statusAtom)

  return useMemo(() => {
    const state = resolveResourceState(items, status, isSignedIn, isResolving)

    return {
      ...state,
      isSettled: state.status !== 'idle' && state.status !== 'loading',
      find: (predicate) => (isSignedIn && items ? items.find(predicate) : undefined),
    }
  }, [items, status, isSignedIn, isResolving])
}

/**
 * Fetches a personalized resource once per session.
 *
 * Requires a confirmed session rather than merely held profile data, because
 * the endpoints derive the uid from the session and answer 401 without one.
 *
 * The status transition is the re-entry guard: moving off NotLoaded happens in
 * the same effect that starts the fetch, so a second run cannot enter. Two of
 * the four fetchers instead carried `shouldFetch` and `hasFetchedOnce` local
 * state, where `hasFetchedOnce` neutered the refetch `shouldFetch` existed to
 * provide, leaving both pairs with the same fetch-once behavior by different
 * means.
 */
export function usePersonalizedResourceFetch<T>(resource: PersonalizedResource<T>): void {
  const { isAuthenticated } = useIdentity()
  const status = useAtomValue(resource.statusAtom)
  const setItems = useSetAtom(resource.itemsAtom)
  const setStatus = useSetAtom(resource.statusAtom)

  const { endpoint, select } = resource

  const load = useCallback(async () => {
    try {
      const response = await fetch(endpoint)
      const json = await response.json()
      setItems(select(json))
      setStatus('loaded')
    } catch (error) {
      setStatus('failed')
      console.error(error)
    }
  }, [endpoint, select, setItems, setStatus])

  useEffect(() => {
    if (!isAuthenticated) return
    if (status !== 'idle') return

    setStatus('loading')
    load()
  }, [isAuthenticated, status, setStatus, load])
}
